import { Checkbox, Divider, Typography } from "antd";
import React, { useEffect, useState } from "react";
import cartService, {
  CartSummary as CartSummaryType,
  Cart,
} from "../../../../../Service/cart.service";
import useAccess from "../../../../../Hooks/useAccess";
import { useAppContext } from "../../../../../Context/AppContext/AppContext";
import PayNowButton from "./PayNowButton";

interface SummaryProps {
  mode: "upgrade" | "addon_only" | "renew";
  selectedAddonId: string | null;
  userCart: Cart;
}

const Summary: React.FC<SummaryProps> = ({
  mode,
  selectedAddonId,
  userCart,
}) => {
  const { currentSubscription, currentPlan } = useAccess();
  const { currentRegion } = useAppContext();

  const isAddonOnlyPurchase =
    !!selectedAddonId && mode?.toLowerCase() === "addon_only";

  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const [summary, setSummary] = useState<CartSummaryType | null>(null);

  const userCurrencyCode = currentRegion === "IND" ? "INR" : "USD";
  const userCurrencySymbol = currentRegion === "IND" ? "₹" : "$";

  const fetchCartSummary = async (
    purchaseType: "addon-only" | "normal",
    addonId: string | null,
  ) => {
    const summary = await cartService.getCartSummary(
      purchaseType,
      addonId,
      userCurrencyCode,
    );
    if (summary) {
      setSummary(summary);
    }
  };

  useEffect(() => {
    const purchaseType = isAddonOnlyPurchase ? "addon-only" : "normal";
    fetchCartSummary(purchaseType, selectedAddonId);

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCart, isAddonOnlyPurchase, selectedAddonId]);

  if (!summary) {
    return <div>Loading...</div>;
  }

  const handleAgree = () => {
    setIsAgreed(!isAgreed);
  };

  const PlanDetails = () => {
    const { selectedPlan, selectedPlanPrice, billingPeriod } = summary;
    return (
      <div className="cart-summary-item-wrapper">
        <div className="cart-summary-item-section">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Plan Details
          </Typography.Title>
        </div>
        <div className="cart-summary-item-section">
          <Typography.Title
            type="secondary"
            level={5}
            style={{ margin: 0, fontWeight: 400 }}
          >
            {selectedPlan?.name} (Billed{" "}
            {billingPeriod === "year" ? "Annually" : "Monthly"})
          </Typography.Title>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {userCurrencySymbol} {selectedPlanPrice}
          </Typography.Title>
        </div>
        {billingPeriod === "year" ? (
          <Typography.Text className="pay-now__plan-note">
            <small>
              (12 × {userCurrencySymbol}
              {Number(selectedPlanPrice) / 12} = {userCurrencySymbol}
              {Number(selectedPlanPrice)} per year)
            </small>
          </Typography.Text>
        ) : null}
      </div>
    );
  };

  const AddonsDetails = () => {
    const {
      applicationAddonCount,
      jobPostsAddonCount,
      applicationAddonPrice,
      jobPostsAddonPrice,
    } = summary;
    return (
      <div className="cart-summary-item-wrapper">
        <div className="cart-summary-item-section">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Addon Details
          </Typography.Title>
        </div>
        {applicationAddonCount > 0 && (
          <div className="cart-summary-item-section">
            <Typography.Title
              type="secondary"
              level={5}
              style={{ margin: 0, fontWeight: 400 }}
            >
              Job Application ({String(applicationAddonCount).padStart(2, "0")})
            </Typography.Title>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {userCurrencySymbol} {applicationAddonPrice}
            </Typography.Title>
          </div>
        )}

        {jobPostsAddonCount > 0 && (
          <div className="cart-summary-item-section">
            <Typography.Title
              type="secondary"
              level={5}
              style={{ margin: 0, fontWeight: 400 }}
            >
              Job Post ({String(jobPostsAddonCount).padStart(2, "0")})
            </Typography.Title>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {userCurrencySymbol} {jobPostsAddonPrice}
            </Typography.Title>
          </div>
        )}
      </div>
    );
  };

  const TotalDetails = () => {
    const { tax, totalPrice, currency } = summary;

    const isOtherRegion = currency !== "INR";

    return (
      <div className="cart-summary-item-wrapper">
        {isOtherRegion && (
          <div className="cart-summary-item-section">
            <Typography.Text style={{ margin: 0 }}>
              <strong>Tax: {userCurrencySymbol}0.00</strong> — No tax has been
              charged as the customer is located outside India and international
              transactions are not subject to Indian tax.
            </Typography.Text>
          </div>
        )}
        {!isOtherRegion && (
          <div className="cart-summary-item-section">
            <Typography.Title level={5} style={{ margin: 0 }}>
              Tax (GST 18%)
            </Typography.Title>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {userCurrencySymbol} {tax}
            </Typography.Title>
          </div>
        )}
        {!isOtherRegion && (
          <div className="cart-summary-item-section">
            <Typography.Title level={5} style={{ margin: 0 }}>
              Total
            </Typography.Title>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {userCurrencySymbol} {totalPrice}
              <br />
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, fontWeight: 400, marginLeft: 4 }}
              >
                (Inc. tax)
              </Typography.Text>
            </Typography.Title>
          </div>
        )}
      </div>
    );
  };

  const TotalPayableDetails = () => {
    const { totalPriceAfterDiscount } = summary;
    return (
      <div className="cart-summary-item-section">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Total Payable
        </Typography.Title>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {userCurrencySymbol} {totalPriceAfterDiscount}
        </Typography.Title>
      </div>
    );
  };

  const PolicyLinksSection = () => {
    return (
      <div className="cart-summary-policy-links-section">
        <Checkbox checked={isAgreed} onChange={handleAgree} />
        <Typography.Text>
          I have read and agree to{" "}
          <Typography.Link
            href="https://www.qualifyme.ai/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms and Conditions
          </Typography.Link>{" "}
          ,{" "}
          <Typography.Link
            href="https://www.qualifyme.ai/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </Typography.Link>{" "}
          and{" "}
          <Typography.Link
            href="https://www.qualifyme.ai/refund"
            target="_blank"
            rel="noopener noreferrer"
          >
            Refund Policy
          </Typography.Link>
        </Typography.Text>
      </div>
    );
  };

  return (
    <div className="cart-summary-content">
      {!isAddonOnlyPurchase && (
        <>
          <PlanDetails />
          <Divider orientation="center" style={{ margin: 0 }} />
        </>
      )}

      {(summary.applicationAddonCount > 0 ||
        summary.jobPostsAddonCount > 0) && (
        <>
          <AddonsDetails />
          <Divider orientation="center" style={{ margin: 0 }} />
        </>
      )}

      <TotalDetails />

      <Divider orientation="center" style={{ margin: 0 }} />

      <TotalPayableDetails />

      <PolicyLinksSection />

      <div className="cart-summary-pay-now-section">
        <PayNowButton
          mode={mode}
          disabled={!isAgreed}
          summary={summary}
          userCurrentPlan={currentPlan}
          userCurrentSubscription={currentSubscription}
          userCart={userCart}
        />
      </div>
    </div>
  );
};

export default Summary;
